import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { CheckCircle, ExternalLink, User, MapPin, Monitor, Clock, FileText, Shield } from 'lucide-react';
import type { EstimateWithRelations } from '@/types/estimate';

interface ViewSignatureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  estimate: EstimateWithRelations | null;
}

export function ViewSignatureDialog({ open, onOpenChange, estimate }: ViewSignatureDialogProps) {
  if (!estimate) return null;

  const hasSignature = estimate.status === 'accepted' && estimate.signature_image_url;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-success" />
            Signature Details
          </DialogTitle>
          <DialogDescription>
            Digital signature and audit trail for estimate {estimate.estimate_number}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Signature Image */}
          {hasSignature && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Signature</p>
              <div className="border rounded-lg p-2 bg-white">
                <img 
                  src={estimate.signature_image_url!} 
                  alt="Customer signature"
                  className="max-h-32 w-full object-contain"
                />
              </div>
            </div>
          )}

          <Separator />

          {/* Signer Details */}
          <div className="grid gap-3">
            <div className="flex items-start gap-3">
              <User className="h-4 w-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Signed by</p>
                <p className="text-sm text-muted-foreground">
                  {estimate.signer_name || 'Unknown'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="h-4 w-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Signed at</p>
                <p className="text-sm text-muted-foreground">
                  {estimate.signed_at 
                    ? format(new Date(estimate.signed_at), "d MMMM yyyy 'at' h:mm a")
                    : 'Unknown'
                  }
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">IP Address</p>
                <p className="text-sm text-muted-foreground font-mono">
                  {estimate.signer_ip_address || 'Unknown'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Monitor className="h-4 w-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Device / Browser</p>
                <p className="text-sm text-muted-foreground break-all">
                  {estimate.signer_user_agent 
                    ? estimate.signer_user_agent.substring(0, 100) + (estimate.signer_user_agent.length > 100 ? '...' : '')
                    : 'Unknown'
                  }
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Consent & Legal */}
          <div className="space-y-2">
            <p className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Consent & Agreements
            </p>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {estimate.signature_consent ? (
                  <Badge variant="outline" className="bg-success/10 text-success border-success/20">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Estimate Terms Accepted
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    Consent not recorded
                  </Badge>
                )}
              </div>

              {estimate.accepted_terms_url && (
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={estimate.accepted_terms_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    View T&C (at time of signing) <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}

              {estimate.accepted_privacy_url && (
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={estimate.accepted_privacy_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    View Privacy Policy (at time of signing) <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Legal Statement */}
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">
              This estimate was digitally signed. The signature, timestamp, IP address, 
              and device information have been recorded for legal verification purposes.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
